// src/features/Planning/components/Step6ProjecttypeandNumberofFloors.jsx
import {
  Box,
  Flex,
  Text,
  Radio,
  RadioGroup,
  Button,
  VStack,
  Divider,
  Badge,
} from "@chakra-ui/react";

const projectTypes = [
  { key: "residential", label: "Residential", icon: "🏠" },
  { key: "commercial", label: "Commercial", icon: "🏢" },
];

const lifestyleTypes = [
  { key: "luxury", label: "Luxury", icon: "✨" },
  { key: "minimal", label: "Minimal", icon: "🪴" },
  { key: "rental-focused", label: "Rental-Focused", icon: "🏘️" },
];

const floorOptions = [
  { key: "ground", label: "Ground Floor", apiNum: 1, sublabel: "G" },
  { key: "ground+1", label: "Ground + 1", apiNum: 2, sublabel: "G+1" },
  { key: "ground+2", label: "Ground + 2", apiNum: 3, sublabel: "G+2" },
  { key: "ground+3", label: "Ground + 3", apiNum: 4, sublabel: "G+3" },
  { key: "ground+4", label: "Ground + 4", apiNum: 5, sublabel: "G+4" },
  { key: "ground+5", label: "Ground + 5", apiNum: 6, sublabel: "G+5" },
];

const getFloorDataCount = (floorsData) => {
  if (!Array.isArray(floorsData)) return 0;
  return floorsData.filter(
    (f) =>
      f &&
      (f.masterBedroomsWithToilet ||
        f.masterBedrooms ||
        (f.additionalSpaces && f.additionalSpaces.length > 0))
  ).length;
};

export default function Step6ProjectType({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const isProjectTypeLocked = !!(
    formData.projectType && formData._prefilled?.projectType
  );
  // NEW: agar API se can_edit_rsform=true aaya hai, to numberOfFloors kabhi
  // lock nahi hoga — user G se G+5 tak koi bhi option select/update kar
  // sakta hai, chahe pehle se prefilled ho. false/missing hone par bilkul
  // purana (locked-when-prefilled) behavior hi chalega — koi change nahi.
  const isFloorLocked = !!(
    formData.numberOfFloors &&
    formData._prefilled?.numberOfFloors &&
    !formData.canEditRsform
  );

  const floorDataCount = getFloorDataCount(formData.floorsData);

  const updateProjectType = (value) => {
    if (isProjectTypeLocked) return;
    setFormData({ ...formData, projectType: value });
  };

  const updateLifestyleType = (value) => {
    setFormData({ ...formData, lifestyleType: value });
  };

  const updateFloors = (value) => {
    if (isFloorLocked) return;
    setFormData({ ...formData, numberOfFloors: value });
  };

  const isValid = () => formData.projectType && formData.numberOfFloors;

  const handleAction = () => {
    if (!viewMode && !isValid()) return;
    isLastStep ? onSubmit() : onNext();
  };

  const selectedFloor = floorOptions.find(
    (f) => f.key === formData.numberOfFloors
  );

  const currentLifestyle = formData.lifestyleType || "minimal";

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "8px" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">

          {/* Heading */}
          <Text
            textAlign="center"
            fontSize="17px"
            fontWeight="600"
            color="gray.800"
            mb={1}
          >
            Confirm Your Project Type & Floors
          </Text>

          {/* ── 1. Lifestyle Type — PEHLE ── */}
          <Box>
            <Text fontSize="14px" fontWeight="600" color="gray.700" mb={2}>
              Lifestyle Type
            </Text>
            <Box
              border="1px"
              borderColor="gray.200"
              borderRadius="8px"
              overflow="hidden"
              bg="white"
            >
              <RadioGroup onChange={updateLifestyleType} value={currentLifestyle}>
                <VStack spacing={0} align="stretch">
                  {lifestyleTypes.map(({ key, label, icon }, index) => (
                    <Box key={key}>
                      <Box
                        px={4}
                        py={2.5}
                        bg={currentLifestyle === key ? "cyan.50" : "white"}
                        onClick={() => updateLifestyleType(key)}
                        cursor="pointer"
                      >
                        <Radio value={key} colorScheme="cyan" size="md" w="full">
                          <Flex align="center" gap={3}>
                            <Text fontSize="18px">{icon}</Text>
                            <Text fontSize="14px" fontWeight="500">{label}</Text>
                          </Flex>
                        </Radio>
                      </Box>
                      {index < lifestyleTypes.length - 1 && <Divider />}
                    </Box>
                  ))}
                </VStack>
              </RadioGroup>
            </Box>
          </Box>

          {/* ── 2. Project Type — BAAD ME ── */}
          <Box>
            <Flex align="center" gap={2} mb={2}>
              <Text fontSize="14px" fontWeight="600" color="gray.700">
                Project Type <Text as="span" color="red.500">*</Text>
              </Text>
              {isProjectTypeLocked && (
                <Badge fontSize="10px" colorScheme="orange" variant="subtle">
                  🔒 Prefilled
                </Badge>
              )}
            </Flex>

            <Box
              border="1px"
              borderColor={isProjectTypeLocked ? "cyan.200" : "gray.200"}
              borderRadius="8px"
              overflow="hidden"
              bg="white"
            >
              {isProjectTypeLocked ? (
                (() => {
                  const selected = projectTypes.find((p) => p.key === formData.projectType);
                  return selected ? (
                    <Flex align="center" gap={3} px={4} py={3} bg="cyan.50">
                      <Text fontSize="20px">{selected.icon}</Text>
                      <Text fontSize="14px" fontWeight="600" color="cyan.700">
                        {selected.label}
                      </Text>
                      <Badge ml="auto" colorScheme="cyan" variant="solid" fontSize="10px" borderRadius="full" px={2}>
                        ✓ Selected
                      </Badge>
                    </Flex>
                  ) : null;
                })()
              ) : (
                <RadioGroup onChange={updateProjectType} value={formData.projectType || ""}>
                  <VStack spacing={0} align="stretch">
                    {projectTypes.map(({ key, label, icon }, index) => (
                      <Box key={key}>
                        <Box
                          px={4}
                          py={2.5}
                          bg={formData.projectType === key ? "cyan.50" : "white"}
                          onClick={() => updateProjectType(key)}
                          cursor="pointer"
                        >
                          <Radio value={key} colorScheme="cyan" size="md" w="full">
                            <Flex align="center" gap={3}>
                              <Text fontSize="20px">{icon}</Text>
                              <Text fontSize="14px" fontWeight="500">{label}</Text>
                            </Flex>
                          </Radio>
                        </Box>
                        {index < projectTypes.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </VStack>
                </RadioGroup>
              )}
            </Box>
          </Box>

          {/* ── 3. Number of Floors ── */}
          <Box>
            <Flex align="center" justify="space-between" mb={2}>
              <Flex align="center" gap={2}>
                <Text fontSize="14px" fontWeight="600">
                  Number of Floors <Text as="span" color="red.500">*</Text>
                </Text>
                {isFloorLocked && (
                  <Badge fontSize="10px" colorScheme="orange" variant="subtle">
                    🔒 Prefilled
                  </Badge>
                )}
              </Flex>
              {selectedFloor && (
                <Badge fontSize="11px" colorScheme="cyan">
                  {selectedFloor.apiNum} floor{selectedFloor.apiNum > 1 ? "s" : ""}
                </Badge>
              )}
            </Flex>

            <Box
              border="1px"
              borderColor={isFloorLocked ? "cyan.200" : "gray.200"}
              borderRadius="8px"
              overflow="hidden"
              bg="white"
            >
              {isFloorLocked ? (
                (() => {
                  const sel = floorOptions.find((f) => f.key === formData.numberOfFloors);
                  return sel ? (
                    <Flex align="center" gap={3} px={4} py={3} bg="cyan.50">
                      <Text fontSize="14px" fontWeight="600" color="cyan.700">
                        {sel.label}
                      </Text>
                      <Badge colorScheme="gray" fontSize="11px" ml={1}>
                        {sel.sublabel}
                      </Badge>
                      <Badge ml="auto" colorScheme="cyan" variant="solid" fontSize="10px" borderRadius="full" px={2}>
                        ✓ Selected
                      </Badge>
                    </Flex>
                  ) : null;
                })()
              ) : (
                <RadioGroup onChange={updateFloors} value={formData.numberOfFloors || ""}>
                  <VStack spacing={0} align="stretch">
                    {floorOptions.map(({ key, label, sublabel }, index) => {
                      const hasData = index < floorDataCount;
                      const isSelected = formData.numberOfFloors === key;
                      return (
                        <Box key={key}>
                          <Box
                            px={4}
                            py={2.5}
                            bg={isSelected ? "cyan.50" : hasData ? "green.50" : "white"}
                            onClick={() => updateFloors(key)}
                            cursor="pointer"
                          >
                            <Radio value={key} colorScheme="cyan" size="md" w="full">
                              <Flex justify="space-between">
                                <Text fontSize="14px">{label}</Text>
                                <Badge fontSize="11px">{sublabel}</Badge>
                              </Flex>
                            </Radio>
                          </Box>
                          {index < floorOptions.length - 1 && <Divider />}
                        </Box>
                      );
                    })}
                  </VStack>
                </RadioGroup>
              )}
            </Box>
          </Box>

        </VStack>
      </Box>

      {/* Buttons */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleAction}
          isDisabled={viewMode ? false : !isValid()}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
          _disabled={{
            bg: "gray.300",
            color: "gray.500",
            cursor: "not-allowed",
          }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}